import { useState, useCallback, useRef, useEffect } from 'react';
import { Rss, Trash2, Loader2, Copy, RefreshCw, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import {
  useCalendarFeeds,
  useCreateCalendarFeed,
  useRotateCalendarFeed,
  useUpdateCalendarFeed,
  useRevokeCalendarFeed,
} from '../hooks/useCalendarFeeds';
import type { CalendarShareVisibility, CalendarFeedMintResult } from '../types';

interface PublishFeedDialogProps {
  open: boolean;
  onClose: () => void;
  calendarId: string | null;
  calendarName?: string;
}

// Same vocabulary as calendar sharing — the feed and the share reuse one
// redactor server-side, so the labels stay identical here too.
const VISIBILITY_LABELS: Record<CalendarShareVisibility, string> = {
  'free-busy': 'Free/busy only',
  titles: 'Titles and times',
  full: 'Full details',
};

const PAST_WINDOW_OPTIONS = [7, 30, 90, 365];
const FUTURE_WINDOW_OPTIONS = [90, 180, 365, 730];

function dayLabel(days: number): string {
  if (days % 365 === 0) return `${days / 365} year${days === 365 ? '' : 's'}`;
  if (days % 30 === 0) return `${days / 30} month${days === 30 ? '' : 's'}`;
  return `${days} days`;
}

export function PublishFeedDialog({
  open,
  onClose,
  calendarId,
  calendarName,
}: PublishFeedDialogProps) {
  const scopedCalendarId = open ? calendarId : null;
  const { data: feeds = [], isLoading } = useCalendarFeeds(scopedCalendarId);
  const createFeed = useCreateCalendarFeed(scopedCalendarId);
  const rotateFeed = useRotateCalendarFeed(scopedCalendarId);
  const updateFeed = useUpdateCalendarFeed(scopedCalendarId);
  const revokeFeed = useRevokeCalendarFeed(scopedCalendarId);

  const [label, setLabel] = useState('');
  const [visibility, setVisibility] = useState<CalendarShareVisibility>('free-busy');
  const [pastDays, setPastDays] = useState(30);
  const [futureDays, setFutureDays] = useState(365);
  // The raw URL is returned exactly once; held in local state and shown once.
  // It is NEVER read back from a feed row (the server can't return it).
  const [minted, setMinted] = useState<CalendarFeedMintResult | null>(null);
  // Bumped on every close. A mint/rotate captures this at request time and
  // refuses to reveal a result whose session has ended — otherwise an in-flight
  // request that resolves AFTER the user closes would resurrect the secret and
  // could surface it under a DIFFERENT calendar on reopen.
  const sessionRef = useRef(0);

  // Defence in depth: a secret must never carry across a calendar change.
  useEffect(() => {
    setMinted(null);
  }, [calendarId]);

  const resetForm = useCallback(() => {
    setMinted(null);
    setLabel('');
    setVisibility('free-busy');
    setPastDays(30);
    setFutureDays(365);
    // The raw URL also lingers in the mutation cache (createFeed.data /
    // rotateFeed.data) until the next mint — clear it too.
    createFeed.reset();
    rotateFeed.reset();
  }, [createFeed, rotateFeed]);

  const handleClose = useCallback(() => {
    sessionRef.current += 1; // invalidate any in-flight reveal
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleCopy = useCallback(async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${what} copied to clipboard` });
    } catch {
      toast({
        title: `Couldn't copy the ${what}`,
        description: 'Select the text and copy it manually before closing.',
        variant: 'destructive',
      });
    }
  }, []);

  const handleCreate = useCallback(async () => {
    const session = sessionRef.current;
    try {
      const result = await createFeed.mutateAsync({
        label: label.trim() || undefined,
        visibility,
        windowPastDays: pastDays,
        windowFutureDays: futureDays,
      });
      if (sessionRef.current !== session) return; // dialog closed mid-request
      setMinted(result);
      setLabel('');
      toast({ title: 'Feed published' });
    } catch (err) {
      if (sessionRef.current !== session) return;
      toast({
        title: 'Failed to publish feed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [createFeed, label, visibility, pastDays, futureDays]);

  const handleRotate = useCallback(
    async (feedId: string, feedLabel: string) => {
      const who = feedLabel ? `"${feedLabel}"` : 'this feed';
      const ok = window.confirm(
        `Rotating ${who} generates a new URL and immediately breaks the old one. Any device already subscribed will stop updating until you give it the new link. Continue?`,
      );
      if (!ok) return;
      const session = sessionRef.current;
      try {
        const result = await rotateFeed.mutateAsync(feedId);
        if (sessionRef.current !== session) return;
        setMinted(result);
        toast({ title: 'Feed URL rotated' });
      } catch (err) {
        if (sessionRef.current !== session) return;
        toast({
          title: 'Failed to rotate',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [rotateFeed],
  );

  const handleUpdateVisibility = useCallback(
    async (
      feedId: string,
      current: CalendarShareVisibility,
      newVisibility: CalendarShareVisibility,
    ) => {
      if (newVisibility === current) return;
      // Escalating an EXISTING feed to full is more consequential than at
      // create time: its URL may already be on devices / shared with people, so
      // this instantly widens what every current token holder can read.
      // Confirm the escalation; reducing visibility needs no confirmation.
      if (newVisibility === 'full') {
        const ok = window.confirm(
          'Full details exposes this calendar’s event descriptions and locations to everyone who already has this feed’s link. Continue?',
        );
        if (!ok) return;
      }
      try {
        await updateFeed.mutateAsync({ feedId, update: { visibility: newVisibility } });
      } catch (err) {
        toast({
          title: 'Failed to update visibility',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [updateFeed],
  );

  const handleRevoke = useCallback(
    async (feedId: string, feedLabel: string) => {
      const who = feedLabel ? `"${feedLabel}"` : 'this feed';
      const ok = window.confirm(
        `Revoke ${who}? Its URL will stop working immediately and any device using it will stop updating. This cannot be undone.`,
      );
      if (!ok) return;
      try {
        await revokeFeed.mutateAsync(feedId);
        // If the one-time panel is showing the feed we just revoked, drop it.
        setMinted((m) => (m && m.feedId === feedId ? null : m));
        toast({ title: 'Feed revoked' });
      } catch (err) {
        toast({
          title: 'Failed to revoke',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [revokeFeed],
  );

  // The device the one-time panel belongs to (a rotate result may not carry the
  // label — fall back to the feed row).
  const mintedLabel = minted
    ? minted.label || feeds.find((f) => f.feedId === minted.feedId)?.label
    : undefined;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rss className="h-4 w-4" />
            Publish {calendarName || 'Calendar'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          Create a read-only link you can add to a phone or another calendar app. The link is a
          secret — anyone who has it can see this calendar at the visibility you choose.
        </p>

        {/* One-time secret — the raw URL is only shown here, once. */}
        {minted && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2 dark:border-amber-900/50 dark:bg-amber-900/20">
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" />
              Copy {mintedLabel ? `the ${mintedLabel} link` : 'this'} now — you won&apos;t be able
              to see it again.
            </div>
            <FeedUrlRow
              label="webcal (tap on iPhone / Mac)"
              value={minted.webcalUrl}
              onCopy={() => void handleCopy(minted.webcalUrl, 'webcal link')}
            />
            <FeedUrlRow
              label="https (paste into Google Calendar)"
              value={minted.url}
              onCopy={() => void handleCopy(minted.url, 'link')}
            />
            <p className="text-[11px] text-amber-700 dark:text-amber-400/80">
              To add this calendar to another device, publish a separate feed for it — each link can
              be revoked on its own.
            </p>
          </div>
        )}

        {/* Create form */}
        <div className="space-y-3">
          <div>
            <Label htmlFor="feed-label" className="text-xs">
              Label
            </Label>
            <Input
              id="feed-label"
              placeholder="e.g. iPhone"
              maxLength={100}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              aria-describedby="feed-label-hint"
            />
            {/* The label doubles as the PUBLIC calendar name at reduced scopes,
                where the real calendar name is withheld. Say so at the moment of
                typing — otherwise people write a private note here and it ends
                up in a subscriber's calendar list. */}
            <p id="feed-label-hint" className="mt-1 text-[11px] text-muted-foreground">
              {visibility === 'full'
                ? 'Private — helps you tell your feed URLs apart.'
                : 'Subscribers see this as the calendar name. Your real calendar name stays private.'}
            </p>
          </div>

          <div>
            <Label htmlFor="feed-visibility" className="text-xs">
              Visibility
            </Label>
            <Select
              value={visibility}
              onValueChange={(v) => setVisibility(v as CalendarShareVisibility)}
            >
              <SelectTrigger id="feed-visibility" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(VISIBILITY_LABELS) as CalendarShareVisibility[]).map((v) => (
                  <SelectItem key={v} value={v}>
                    {VISIBILITY_LABELS[v]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {visibility === 'full' && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400/80">
              Full details puts event descriptions and locations behind an unauthenticated link.
              Only choose this if you trust everyone who will have it.
            </p>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Label htmlFor="feed-past" className="text-xs">
                Show past
              </Label>
              <Select value={String(pastDays)} onValueChange={(v) => setPastDays(parseInt(v, 10))}>
                <SelectTrigger id="feed-past" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAST_WINDOW_OPTIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {dayLabel(d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label htmlFor="feed-future" className="text-xs">
                Show ahead
              </Label>
              <Select
                value={String(futureDays)}
                onValueChange={(v) => setFutureDays(parseInt(v, 10))}
              >
                <SelectTrigger id="feed-future" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUTURE_WINDOW_OPTIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {dayLabel(d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            The window limits one-off events and how much the feed carries. Recurring events are
            always published in full and repeat on the device across all dates — including outside
            this window.
          </p>

          <Button size="sm" onClick={handleCreate} disabled={createFeed.isPending}>
            {createFeed.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Publish feed
          </Button>
        </div>

        {/* Existing feeds */}
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : feeds.length > 0 ? (
          <div className="space-y-2 mt-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Published feeds
            </div>
            {feeds.map((feed) => (
              <div
                key={feed.feedId}
                className="flex items-center gap-2 rounded-lg border bg-card p-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{feed.label || 'Unnamed feed'}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {feed.lastServedAt
                      ? `last used ${new Date(feed.lastServedAt).toLocaleDateString()}`
                      : 'never used'}
                  </div>
                </div>
                <Select
                  value={feed.visibility}
                  // Shared mutation → disable EVERY row's selector while any
                  // update is in flight, so two rows can't race and a feed can't
                  // land on a stale value via out-of-order completion.
                  disabled={updateFeed.isPending}
                  onValueChange={(v) =>
                    handleUpdateVisibility(
                      feed.feedId,
                      feed.visibility,
                      v as CalendarShareVisibility,
                    )
                  }
                >
                  <SelectTrigger className="w-40 h-7 text-xs" aria-label="Feed visibility">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(VISIBILITY_LABELS) as CalendarShareVisibility[]).map((v) => (
                      <SelectItem key={v} value={v}>
                        {VISIBILITY_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Rotate URL (breaks the old link)"
                  disabled={rotateFeed.isPending}
                  onClick={() => handleRotate(feed.feedId, feed.label)}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  title="Revoke feed"
                  disabled={revokeFeed.isPending}
                  onClick={() => handleRevoke(feed.feedId, feed.label)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-2">No feeds published yet</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FeedUrlRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] text-amber-700 dark:text-amber-400/80">{label}</div>
      <div className="flex items-center gap-1.5">
        <code
          className="flex-1 truncate rounded bg-background/60 px-2 py-1 text-[11px]"
          title={value}
        >
          {value}
        </code>
        <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={onCopy}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
